import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import {
  DEFAULT_SITE_THEME_ID,
  SITE_THEMES,
  isValidSiteThemeId,
  persistSiteThemeId,
  readStoredSiteThemeId,
  siteThemeHtmlClass,
} from "../theme/siteThemes";

const SiteThemeContext = createContext(null);

function applyThemeToDocument(themeId) {
  const el = document.documentElement;
  SITE_THEMES.forEach((t) => el.classList.remove(siteThemeHtmlClass(t.id)));
  el.classList.add(siteThemeHtmlClass(themeId));
  el.dataset.pbSiteTheme = themeId;
}

export function SiteThemeProvider({ children }) {
  const [themeId, setThemeIdState] = useState(readStoredSiteThemeId);

  const setThemeId = useCallback((next) => {
    const id = isValidSiteThemeId(next) ? next : DEFAULT_SITE_THEME_ID;
    setThemeIdState(id);
    persistSiteThemeId(id);
    applyThemeToDocument(id);
  }, []);

  useEffect(() => {
    applyThemeToDocument(themeId);
  }, [themeId]);

  useEffect(() => {
    const onStorage = (e) => {
      if (e.key !== "partyBossSiteTheme" || !isValidSiteThemeId(e.newValue)) return;
      setThemeIdState(e.newValue);
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  const value = useMemo(
    () => ({
      themeId,
      themes: SITE_THEMES,
      setThemeId,
      activeTheme: SITE_THEMES.find((t) => t.id === themeId) || SITE_THEMES[0],
    }),
    [themeId, setThemeId]
  );

  return <SiteThemeContext.Provider value={value}>{children}</SiteThemeContext.Provider>;
}

export function useSiteTheme() {
  const ctx = useContext(SiteThemeContext);
  if (!ctx) {
    throw new Error("useSiteTheme must be used within SiteThemeProvider");
  }
  return ctx;
}
